import { useAuth0 } from "@auth0/auth0-react";
import { Button, IconButton } from "@chakra-ui/react/button";
import { createListCollection } from "@chakra-ui/react/collection";
import { useBreakpointValue } from "@chakra-ui/react/hooks";
import { Icon } from "@chakra-ui/react/icon";
import { Portal } from "@chakra-ui/react/portal";
import { Select } from "@chakra-ui/react/select";
import { HStack, VStack } from "@chakra-ui/react/stack";
import { Switch } from "@chakra-ui/react/switch";
import { Text } from "@chakra-ui/react/text";

import { useColorMode } from "components/ui/color-mode";
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogRoot,
} from "components/ui/dialog";
import { toaster } from "components/ui/toaster";
import { useCurrentAgent } from "hooks/queries/use-current-agent";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { LuMoon, LuSun } from "react-icons/lu";
import { setColorScheme } from "redux/features/theme";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import AgentsService from "services/agents";
import { LocalStorage } from "util/localStorage";

interface StylePreferencesProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface OptionTypes {
  value: string;
  label: string;
}

const StylePreferences = ({ isOpen, setIsOpen }: StylePreferencesProps) => {
  const { colorScheme } = useAppSelector((state) => state.theme);
  const dispatch = useAppDispatch();
  const auth0Context = useAuth0();
  const { data: currentAgent } = useCurrentAgent();
  const { data: merchant } = useMerchant();
  const { colorMode, toggleColorMode } = useColorMode();
  const isBaseSize = useBreakpointValue(
    { base: true, md: false },
    { ssr: false }
  );
  const contentRef = useRef<HTMLDivElement>(null);
  const soundSwitchId = useId();

  const [colorSchemeValue, setColorSchemeValue] = useState<string>(
    currentAgent?.stylePreferences?.colorScheme || "blue"
  );

  const [isNotificationSoundEnabled, setIsNotificationSoundEnabled] =
    useState<boolean>(LocalStorage.isNotificationSoundEnabled());

  const colorSchemeValueOptions: {
    value: string;
    label: string;
  }[] = [
    { value: "blue", label: "Fuzey Blue" },
    { value: "yellow", label: "Yellow" },
    { value: "green", label: "Green" },
    { value: "purple", label: "Purple" },
    { value: "pink", label: "Pink" },
    { value: "cyan", label: "Cyan" },
    { value: "teal", label: "Teal" },
  ];

  const colorSchemeCollection = useMemo(
    () =>
      createListCollection({
        items: colorSchemeValueOptions,
      }),
    [colorSchemeValueOptions]
  );

  useEffect(() => {
    if (colorSchemeValue === colorScheme) {
      return;
    }

    dispatch(setColorScheme(colorSchemeValue));
  }, [colorSchemeValue]);

  useEffect(() => {
    if (!currentAgent) {
      return;
    }

    setColorSchemeValue(currentAgent.stylePreferences?.colorScheme || "blue");
  }, [currentAgent]);

  const [isSaving, setIsSaving] = useState<boolean>(false);

  const updateAgent = async () => {
    if (!currentAgent || !merchant?.id) {
      toaster.create({
        title: "Unable to save style preferences yet",
        type: "error",
      });
      return;
    }

    setIsSaving(true);

    try {
      await AgentsService.updateAgent(
        auth0Context,
        {
          id: currentAgent.id,
          name: currentAgent.name,
          surname: currentAgent.surname,
          email: currentAgent.email,
          role_id: currentAgent.getRole(merchant.id).id,
          active: currentAgent.isActive(merchant.id),
          profile_picture_url: currentAgent.getPicture(),
          player_ids: currentAgent.playerIds || null,
          style_preferences: {
            chat_background:
              currentAgent.stylePreferences?.chatBackground || null,
            color_scheme: colorSchemeValue,
          },
        },
        merchant.id
      );
      setIsOpen(false);
    } catch (e: unknown) {
      toaster.create({
        title: "Error occured while saving style preferences",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DialogRoot
      size={isBaseSize ? "full" : "lg"}
      open={isOpen}
      onOpenChange={({ open: newIsOpen }) => setIsOpen(newIsOpen)}
    >
      <DialogContent ref={contentRef}>
        <DialogHeader>Style Preferences</DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          <VStack mb={4} w="100%">
            <HStack w="100%" mb={4}>
              <Select.Root
                collection={colorSchemeCollection}
                disabled={isSaving}
                value={colorSchemeValue ? [colorSchemeValue] : []}
                onValueChange={({ value }) =>
                  setColorSchemeValue(value[0] || "blue")
                }
                colorPalette={colorScheme}
                size="md"
                width="100%"
                deselectable={false}
              >
                <Select.HiddenSelect />
                <Select.Control>
                  <Select.Trigger>
                    <Select.ValueText placeholder="Select color scheme" />
                  </Select.Trigger>
                  <Select.IndicatorGroup>
                    <Select.Indicator />
                  </Select.IndicatorGroup>
                </Select.Control>
                <Portal container={contentRef}>
                  <Select.Positioner>
                    <Select.Content>
                      {colorSchemeCollection.items.map((option) => (
                        <Select.Item item={option} key={option.value}>
                          {option.label}
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Portal>
              </Select.Root>

              <IconButton
                size="md"
                aria-label="Toggle color mode"
                onClick={toggleColorMode}
              >
                <Icon as={colorMode === "dark" ? LuMoon : LuSun} />
              </IconButton>
            </HStack>
            <HStack w="100%" mb={4} justifyContent="space-between">
              <Text>Notification sound</Text>
              <Switch.Root
                checked={isNotificationSoundEnabled}
                onCheckedChange={({ checked }) => {
                  LocalStorage.setNotificationSoundEnabled(checked);
                  setIsNotificationSoundEnabled(checked);
                }}
                size="md"
                colorPalette={colorScheme}
                ids={{ hiddenInput: soundSwitchId }}
                role="switch"
              >
                <Switch.HiddenInput id={soundSwitchId} />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
            </HStack>
            <Button
              alignSelf="end"
              colorPalette={colorScheme}
              size="md"
              onClick={updateAgent}
              disabled={isSaving}
            >
              Save
            </Button>
          </VStack>
        </DialogBody>
      </DialogContent>
    </DialogRoot>
  );
};

export default StylePreferences;
